import { datadogLogs } from '@datadog/browser-logs';
import { asadoSdk } from '@fff-web/asado-sdk';
import type { Env } from '@fff-web/constants';
import { appEnv } from '../utils/env';

// spa=true → manual redirect + credentials included, so the browser sends the Jwt cookie asado
// reads in `extractTokenFromHeader`. Mirrors the universal-navigation `useNavbarRoutes` init.
//
// Built per call, not at module scope: `asadoSdk` throws on a falsy env and `REACT_APP_ENVIRONMENT`
// is unset under test, so at module scope any test file transitively reaching this one would crash
// on import. It only closes over config, so constructing it per call is free.
const getAsadoSdk = () =>
  asadoSdk(appEnv as Env, true, undefined, {
    platform: 'web-legacy',
    version: `${process.env.APP_NAME ?? 'my-account'}@${process.env.APP_VERSION ?? 'unknown'}`,
  });

type CacheBust = [label: string, run: () => Promise<{ ok: boolean; status: number }>];

/**
 * Fires every bust and reports each outcome independently. Best-effort: a failed bust only
 * re-exposes staleness that self-heals at TTL, so it never surfaces to the caller — the log is its
 * only signal. Hence checking `ok`, not just rejection: `createRequest` resolves with
 * `{ ok: false }` for non-2xx and synthesises a 504 on timeout, so HTTP failures never reject.
 *
 * The thunks are invoked inside an `async` wrapper so that a *synchronous* throw becomes a rejection
 * too — building the SDK throws when the env is missing, which must be logged like any other
 * failure rather than escaping to a caller that has no catch (it never blocks a mutation).
 */
const runBusts = async (busts: CacheBust[]): Promise<void> => {
  const results = await Promise.allSettled(busts.map(async ([, run]) => run()));

  results.forEach((result, index) => {
    const [cache] = busts[index];

    if (result.status === 'rejected') {
      datadogLogs.logger.warn(
        'Member cache invalidation failed',
        { cache },
        result.reason instanceof Error ? result.reason : new Error(String(result.reason)),
      );
      return;
    }

    if (!result.value?.ok) {
      datadogLogs.logger.warn('Member cache invalidation failed', {
        cache,
        status: result.value?.status,
      });
    }
  });
};

/**
 * Best-effort bust of the navbar-profile BFF cache alone. Use this where the menu-data cache is
 * already being invalidated by another path — `helpers/invalidateCaches`, which busts it through
 * `@fff-web/fff-utilities` — so the same DELETE is not issued twice.
 */
export const invalidateNavbarProfileCache = async (): Promise<void> => {
  await runBusts([['navbar-profile', () => getAsadoSdk().invalidateNavbarProfile()]]);
};

/**
 * Best-effort bust of both per-member asado BFF caches. Call after a mutation that changes a field
 * either one renders — avatar upload and personal-info edit — so the header stops showing the
 * pre-save value for the rest of the cache's fresh window.
 *
 * Both are needed, because the two flows' fields live in both payloads:
 *   - navbar-profile → `userAvatarUrl`, `firstName`, `lastName` (the maui navbar)
 *   - menu-data → `avatar`, `user.firstName`, `user.lastName`, `user.email` — read through
 *     `@fff-web/fff-utilities`' user provider into `useUserContext`, which backs the legacy global
 *     nav's profile picture and name across this app
 */
export const invalidateMemberCaches = async (): Promise<void> => {
  await runBusts([
    ['navbar-profile', () => getAsadoSdk().invalidateNavbarProfile()],
    ['menu-data', () => getAsadoSdk().invalidateMenuData()],
  ]);
};
